import { createSelector } from "@reduxjs/toolkit";
import type {
  StorageScope,
  TValueForService,
} from "../../../services/api/instances/storageApi/types/service_types";
import type { RootState } from "../../../services/redux/types";
import { storageObjectAdapter } from "./adapter";
import type { ServiceField } from "./types";
import { denormalizeStorageObjects } from "./utils/denormalizeObjectsTree";
import { MenuItem, MenuItems } from "../../../common/AppSidebar/types";
import {
  StorageObject,
  NormalizedStorageObject,
} from "../../../services/api/instances/storageApi/types/object_types";
import { compareByName } from "../../../shared/utils/compareByName";
import { aggregatePermissions } from "./utils/aggregatePermissions";
import { EMPTY_MENU_ITEMS, EMPTY_OBJECTS } from "./constants";

const { selectAll, selectById, selectEntities, selectIds } =
  storageObjectAdapter.getSelectors();

export const selectStorageState = (state: RootState) =>
  state.storageObjectState;

export const selectCurrentService = createSelector(
  selectStorageState,
  (state) => state.currentService,
);

export const getServiceState = (state: RootState, service: StorageScope) =>
  state.storageObjectState[service];

export const makeSelectServiceField = <K extends ServiceField>(
  service: StorageScope,
  field: K,
) =>
  createSelector(
    [(state: RootState) => getServiceState(state, service)],
    (serviceState) => {
      return serviceState?.[field] ?? null;
    },
  );

export const makeSelectMenuItems = <S extends StorageScope>(scope: S) =>
  createSelector(
    [
      (state: RootState) => state.storageObjectState[scope]?.data,
      (state: RootState) => state.storageObjectState.sharedObjects,
    ],
    (data, sharedObjects): MenuItems => {
      if (!data) return EMPTY_MENU_ITEMS;
      const denormalized = denormalizeStorageObjects(
        data.ids,
        data.entities,
      ) as StorageObject<TValueForService<S>>[];

      const mapToMenuItem = (
        obj: StorageObject<TValueForService<S>>,
      ): MenuItem => {
        return {
          id: obj.id,
          name: obj.friendly_name,
          children:
            obj.children?.map(mapToMenuItem).sort(compareByName) || null,
          parentId: obj.parentId ?? null,
          value: obj.value,
          isReceivedObject: !obj?.owner,
          isSharedObject: sharedObjects
            ? sharedObjects.some(
                (shared) =>
                  String(shared.object_id) === obj.id ||
                  String(shared.object_id) === obj.parentId,
              )
            : false,
          readOnly: !obj?.write,
        };
      };

      return denormalized
        .map(mapToMenuItem)
        .sort((a, b) => Number(!b.value) - Number(!a.value));
    },
  );

export const makeSelectAllObjects = <S extends StorageScope>(service: S) =>
  createSelector(
    [
      (state: RootState) => state.storageObjectState[service]?.data.ids,
      (state: RootState) => state.storageObjectState[service]?.data.entities,
    ],
    (ids, entities): NormalizedStorageObject<TValueForService<S>>[] => {
      if (!ids || !entities) return EMPTY_OBJECTS;
      return selectAll({ ids, entities }) as NormalizedStorageObject<
        TValueForService<S>
      >[];
    },
  );

export const selectSharedObjects = createSelector(
  selectStorageState,
  (state) => {
    if (!state.sharedObjects) return [];
    return state.sharedObjects;
  },
);

export const selectSharedObjectById = createSelector(
  selectSharedObjects,
  (_: RootState, id: string) => id,
  (sharedObjects, id) => aggregatePermissions(sharedObjects, id),
);

export const makeSelectSelectedObject = <S extends StorageScope>(service: S) =>
  createSelector(
    [
      (state: RootState) => state.storageObjectState[service]?.data,
      (state: RootState) => state.storageObjectState[service]?.selectedId,
    ],
    (data, selectedId) => {
      if (!data || !selectedId) {
        return null;
      }
      const obj = selectById(data, selectedId) as NormalizedStorageObject<
        TValueForService<S>
      > | null;
      return obj;
    },
  );

export const makeSelectSelectedObjectVersion = (service: StorageScope) =>
  createSelector(
    [(state: RootState) => getServiceState(state, service)],
    (serviceState) => {
      if (!serviceState) {
        return null;
      }
      const { versions, selectedId, selectedVersionId } = serviceState;
      if (!versions || selectedId === null) return null;

      return versions[selectedId]?.find(
        (version) => version.version_id === selectedVersionId,
      );
    },
  );
